import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Phone, FilePdf, Trophy, CurrencyEur, Calendar,
  SignOut, CheckCircle, ListChecks, ArrowRight, EnvelopeSimple,
  Medal, Crown, BookOpen, UserMinus, ListBullets, CaretDown, CaretUp, ShieldCheck
} from "@phosphor-icons/react";
import { teleproApi, TELEPRO_TOKEN_KEY, teleproLogout } from "../lib/teleproApi";
import TeleproTutorial from "../components/TeleproTutorial";
import TeleproCallList from "../components/TeleproCallList";
import ProGmailCard from "../components/ProGmailCard";
import TeleproContract from "../components/TeleproContract";
import TeleproAbsenceCard from "../components/TeleproAbsenceCard";
import TeleproDailyGoalCard from "../components/TeleproDailyGoalCard";
import AvenantModal from "../components/AvenantModal";
import TeleproSiretCard from "../components/TeleproSiretCard";
import OnboardingTour from "../components/OnboardingTour";
import InstallPWAButton from "../components/InstallPWAButton";
import { StreakBadge } from "../components/telepro/StreakBadge";
import EndOfDayRecapModal from "../components/telepro/EndOfDayRecapModal";
import EndOfWeekRecapModal from "../components/telepro/EndOfWeekRecapModal";
import CommissionExportCard from "../components/telepro/CommissionExportCard";
import MobileBottomNav from "../components/telepro/MobileBottomNav";
import TeleproAlertsBanner from "../components/telepro/TeleproAlertsBanner";
import TeleproCommissionCard from "../components/telepro/TeleproCommissionCard";
import TeleproQualificationCard from "../components/telepro/TeleproQualificationCard";
import TeleproPerformanceCard from "../components/telepro/TeleproPerformanceCard";
import SalesAdviceCard from "../components/telepro/SalesAdviceCard";
import TrainingCard from "../components/telepro/TrainingCard";
import RecordsStrip from "../components/telepro/RecordsStrip";
import TeleproDailyReminder from "../components/telepro/TeleproDailyReminder";
import TeleproShareLinkCard from "../components/telepro/TeleproShareLinkCard";

/**
 * /teleprospecteur/portail — read-only personal dashboard.
 *
 * Sections:
 *   - Hero: greeting + telepro code + objectif quotidien
 *   - Today block: today's list status + big PDF download button
 *   - KPIs: 4 commission cards (this month / total signups / total monthly recur / grand total)
 *   - Recent sales table
 *   - History of past lists
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TeleproPortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [dash, setDash] = useState(null);
  const [lists, setLists] = useState([]);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [interactiveListId, setInteractiveListId] = useState(null);
  const [endOfDayOpen, setEndOfDayOpen] = useState(false);
  const [endOfWeekOpen, setEndOfWeekOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TELEPRO_TOKEN_KEY);
    if (!token) {
      navigate("/teleprospecteur", { replace: true });
      return;
    }
    Promise.all([
      teleproApi.get("/me"),
      teleproApi.get("/me/dashboard"),
      teleproApi.get("/me/lists?limit=15"),
      teleproApi.get("/me/leaderboard"),
    ])
      .then(([r1, r2, r3, r4]) => {
        setMe(r1.data.telepro);
        setDash(r2.data);
        setLists(r3.data.items || []);
        setBoard(r4.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          navigate("/teleprospecteur", { replace: true });
        } else {
          setError(err?.response?.data?.detail || "Erreur de chargement");
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    teleproLogout();
    navigate("/teleprospecteur", { replace: true });
  };

  const deactivateSelf = async () => {
    if (!window.confirm("Veux-tu vraiment quitter l'équipe ? Ton compte sera désactivé. Tu pourras demander à un admin de te réactiver plus tard.")) return;
    try {
      await teleproApi.post("/me/deactivate");
      teleproLogout();
      navigate("/teleprospecteur", { replace: true });
    } catch {
      setError("Désactivation impossible. Contacte l'administrateur.");
    }
  };

  const downloadPdf = async (listId, code, date) => {
    // Inline-streamed PDF — open in new tab with bearer header is tricky in
    // browsers, so we fetch as blob then click an <a> programmatically.
    try {
      const res = await teleproApi.get(`/me/lists/${listId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `liste-${code}-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Téléchargement du PDF impossible");
    }
  };

  const refuseList = async (listId) => {
    if (!window.confirm(
      "Refuser cette liste ?\n\n" +
      "Les prospects seront libérés et redistribués demain (à toi ou à un autre " +
      "téléprospecteur). Tu seras automatiquement marqué(e) absent(e) pour la journée."
    )) return;
    try {
      const res = await teleproApi.post(`/me/lists/${listId}/refuse`);
      const n = res.data.released_prospects || 0;
      alert(`Liste refusée. ${n} prospects libérés.`);
      // Refresh dashboard + telepro state
      const [r1, r2, r3] = await Promise.all([
        teleproApi.get("/me"),
        teleproApi.get("/me/dashboard"),
        teleproApi.get("/me/lists?limit=15"),
      ]);
      setMe(r1.data.telepro);
      setDash(r2.data);
      setLists(r3.data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Refus impossible.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"/>
      </div>
    );
  }

  if (error && !me) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12 text-center">
        <p className="text-secondary mb-4">{error}</p>
        <button onClick={logout} className="text-sm underline">Se reconnecter</button>
      </div>
    );
  }

  const kpis = dash?.kpis || {};
  const today = dash?.today;
  const todayDate = dash?.today_date;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8" data-testid="telepro-portal">
      <Helmet>
        <title>{`Portail — ${me?.first_name || ""} ${me?.last_name || ""} — MonCommerce68`}</title>
        <meta name="robots" content="noindex,nofollow"/>
      </Helmet>

      {/* AVENANT — modal bloquante pour les 2 signataires historiques (Carine, Léa) */}
      <AvenantModal onSigned={() => {
        // Refresh le profil pour hydrater avenant_signed_at et fermer la modal
        teleproApi.get("/me").then((r) => setMe(r.data.telepro)).catch(() => {});
      }}/>

      {/* ONBOARDING — tour guidé à la 1re connexion (skip si déjà fait) */}
      <OnboardingTour
        steps={[
          {
            testid: "call-mode-switcher",
            title: "Choisis ta façon de bosser 🎯",
            body: "Sur téléphone, le mode « Simple » te montre une seule fiche à la fois avec de gros boutons. Sur PC, le mode « Liste » te permet de tout voir d'un coup. Tu peux switcher quand tu veux — ton choix est mémorisé.",
          },
          {
            testid: "simple-call-phone",
            title: "Appelle en un tap 📞",
            body: "Le gros bouton vert lance l'appel directement — pas besoin de copier le numéro. Si l'entreprise n'a pas de téléphone dans la fiche, un bouton rouge « Chercher sur Google » ouvre une recherche pré-remplie pour trouver ses coordonnées en 5 secondes.",
          },
          {
            testid: "simple-btn-sale",
            title: "Note le résultat après chaque appel ✅",
            body: "Après avoir raccroché, choisis un des 4 gros boutons colorés : Vente (bravo !), Rappeler plus tard, Pas joint, ou Refus. La fiche suivante s'ouvre automatiquement. Simple comme bonjour.",
          },
          {
            testid: "simple-btn-open-dnc",
            title: "« Ne me rappelez plus jamais » 🚫",
            body: "Si un commerçant demande à ne PLUS être contacté (RGPD), utilise ce bouton discret en bas. On te demandera confirmation. L'entreprise sera bloquée pour toutes les téléprospectrices — jamais dans une autre liste.",
          },
        ]}
      />

      {/* PWA — invite à installer sur l'écran d'accueil (uniquement mobile, dismiss 30j) */}
      <div className="mb-4">
        <InstallPWAButton/>
      </div>

      {/* HERO */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="label-uppercase mb-2">Portail Téléprospection</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium leading-tight">
            Bonjour {me?.first_name} 👋
          </h1>
          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
            <span>Code : <b className="text-foreground font-mono">{me?.code}</b></span>
            <span className="text-border">·</span>
            <span>Objectif quotidien : <b className="text-foreground tabular-nums">{me?.daily_goal}</b> appels</span>
          </div>
        </div>
        <button
          onClick={logout}
          className="self-start sm:self-auto text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 border border-border rounded-sm px-3 py-1.5"
          data-testid="telepro-logout"
        >
          <SignOut size={12} weight="bold"/> Se déconnecter
        </button>
      </div>

      {/* CONTRACT NOT SIGNED — persistent orange banner at the very top */}
      {me && !me.contract_signed_at && (
        <a
          href="#contract-section"
          className="block bg-secondary/10 border-l-4 border-secondary rounded-sm p-3 sm:p-4 mb-6 hover:bg-secondary/15 transition"
          data-testid="contract-unsigned-banner-top"
        >
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 grid place-items-center rounded-sm bg-secondary text-secondary-foreground shrink-0 font-bold">⚠️</span>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm sm:text-base text-secondary mb-0.5">
                Ton contrat de partenariat n&apos;est pas encore signé
              </div>
              <p className="text-xs sm:text-[13px] text-foreground/90 leading-relaxed">
                Tu peux travailler en attendant, mais <b>signe ton contrat dès que possible</b> pour que tes commissions soient validées.
                <span className="underline ml-1 text-secondary">Aller à la section contrat →</span>
              </p>
            </div>
          </div>
        </a>
      )}

      {/* PRO GMAIL CONFIG CARD */}
      <ProGmailCard me={me} onUpdated={setMe}/>

      {/* Vague 34 — Daily reminder (4 golden rules) */}
      <TeleproDailyReminder teleproCode={me?.code} />

      {/* Vague 35 — Share link to send prospects */}
      <TeleproShareLinkCard teleproCode={me?.code} />

      {/* Vague 30A — Proactive alerts banner */}
      <TeleproAlertsBanner onCta={(a) => {
        if (a?.cta?.route === "/teleprospecteur/liste" && dash?.today?.id) {
          setInteractiveListId(dash.today.id);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }} />

      {/* Vague 36 — Live commission dashboard (big € number, trend, rank, top 3) */}
      <TeleproCommissionCard />

      {/* Vague 42 — Refus par motif + leads chauds */}
      <TeleproQualificationCard />

      {/* Vague 43 — Performance 7/30 j + avant/après */}
      <TeleproPerformanceCard />

      {/* Vague 44 — Mes conseils commerciaux */}
      <SalesAdviceCard />

      {/* Vague 46 — Formation interactive (jeu de rôle IA) */}
      <TrainingCard />

      {/* DAILY GOAL CONFIG */}
      <TeleproDailyGoalCard me={me} onUpdated={setMe}/>

      {/* ABSENCE MANAGEMENT */}
      <TeleproAbsenceCard me={me} onUpdated={(updated) => {
        setMe(updated);
        // If user just declared an absence covering today, hide today's list
        // immediately by refreshing the dashboard.
        teleproApi.get("/me/dashboard").then((r) => setDash(r.data)).catch(() => {});
        teleproApi.get("/me/lists?limit=15").then((r) => setLists(r.data.items || [])).catch(() => {});
      }}/>

      {/* TODAY BLOCK */}
      <div className="bg-card border border-border rounded-sm p-5 mb-8 shadow-sm" data-testid="telepro-today-block">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0 basis-full sm:basis-auto">
            <div className="w-10 h-10 shrink-0 bg-primary/10 text-primary rounded-sm grid place-items-center">
              <Calendar size={18} weight="duotone"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <div className="label-uppercase">Ta liste du jour</div>
                <StreakBadge refreshKey={dash?.today?.count || 0}/>
              </div>
              <div className="font-display text-lg font-medium tabular-nums">{todayDate}</div>
              {today ? (
                <p className="text-sm text-muted-foreground mt-1">
                  <b className="text-foreground tabular-nums">{today.count}</b> entreprises à contacter aujourd&apos;hui.
                  {today.email_status === "sent" && (
                    <span className="ml-2 inline-flex items-center gap-1 text-primary text-xs">
                      <EnvelopeSimple size={11} weight="bold"/> Email envoyé
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">
                  Aucune liste générée pour aujourd&apos;hui. L&apos;admin la prépare en début de matinée.
                </p>
              )}
              {today && dash?.untreated_today > 0 && (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/30 rounded-sm px-2 py-1 text-[11px] font-semibold"
                  data-testid="untreated-badge"
                >
                  👋 Il te reste <b className="tabular-nums">{dash.untreated_today}</b> {dash.untreated_today > 1 ? "prospects non traités" : "prospect non traité"}
                </div>
              )}
            </div>
          </div>
          {today && (
            <div className="flex flex-row gap-2 shrink-0 w-full sm:w-auto">
              <button
                onClick={() => setInteractiveListId(interactiveListId === today.id ? null : today.id)}
                className="flex-1 sm:flex-none bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-3 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2"
                data-testid="telepro-open-today-interactive"
              >
                <ListBullets size={16} weight="bold"/>
                {interactiveListId === today.id ? "Replier" : "Cocher mes appels"}
                {interactiveListId === today.id ? <CaretUp size={12} weight="bold"/> : <CaretDown size={12} weight="bold"/>}
              </button>
              <button
                onClick={() => downloadPdf(today.id, me?.code, today.date || todayDate)}
                className="bg-card text-foreground border border-border hover:bg-accent px-4 py-3 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2"
                data-testid="telepro-download-today"
              >
                <FilePdf size={16} weight="bold"/> PDF
              </button>
              <button
                onClick={() => refuseList(today.id)}
                title="Refuser la liste — les prospects seront redistribués demain"
                className="bg-card text-muted-foreground border border-border hover:border-destructive hover:text-destructive px-3 py-3 rounded-sm text-xs inline-flex items-center justify-center gap-1.5"
                data-testid="telepro-refuse-today"
              >
                <UserMinus size={14} weight="bold"/> Refuser
              </button>
            </div>
          )}
        </div>
        {interactiveListId === today?.id && (
          <div className="mt-5 pt-5 border-t border-border" data-testid="today-interactive-panel">
            <TeleproCallList listId={today.id} dailyGoal={me?.daily_goal || 60} telepro={me} onClose={() => setInteractiveListId(null)}/>
            {/* Vague 11-C — Bouton fin de journée + Vague 11-E — Bouton fin de semaine */}
            <div className="mt-6 pt-5 border-t border-border/60 flex flex-col items-center gap-3">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  onClick={() => setEndOfDayOpen(true)}
                  className="bg-gradient-to-r from-primary to-secondary text-primary-foreground hover:opacity-90 rounded-sm px-5 py-3 text-sm font-bold inline-flex items-center gap-2 shadow-md"
                  data-testid="end-of-day-btn"
                >
                  🏁 Terminer ma journée · Bilan IA
                </button>
                <button
                  onClick={() => setEndOfWeekOpen(true)}
                  className="bg-card border-2 border-secondary text-secondary hover:bg-secondary/10 rounded-sm px-5 py-3 text-sm font-bold inline-flex items-center gap-2 shadow-sm"
                  data-testid="end-of-week-btn"
                >
                  🗓 Terminer ma semaine · Bilan IA
                </button>
              </div>
              <div className="text-[11px] text-muted-foreground italic text-center max-w-md">
                Le bilan journée regarde tes appels d'aujourd'hui.
                Le bilan semaine analyse tous les jours depuis lundi — clique quand tu es vraiment en fin de semaine.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* KPIS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="telepro-kpis">
        <Kpi label="Ce mois" value={`${kpis.this_month_commissions_eur?.toFixed(2) || "0.00"} €`} icon={<CurrencyEur size={16} weight="duotone"/>} accent testid="kpi-month"/>
        <Kpi label="Ventes actives" value={kpis.sales_active || 0} icon={<Trophy size={16} weight="duotone"/>} testid="kpi-sales-active"/>
        <Kpi label="Commissions vente (cumul)" value={`${kpis.total_sale_commissions_eur?.toFixed(2) || "0.00"} €`} icon={<CheckCircle size={16} weight="duotone"/>} testid="kpi-total-sale"/>
        <Kpi label="Total reçu (cumul)" value={`${kpis.grand_total_eur?.toFixed(2) || "0.00"} €`} icon={<Trophy size={16} weight="duotone"/>} testid="kpi-grand-total"/>
      </div>

      {/* Vague 30B — Records personnels */}
      <RecordsStrip records={kpis.records} />

      {/* Vague 12 — Export commissions (comptable) */}
      <div className="mb-8">
        <CommissionExportCard/>
      </div>

      {/* LEADERBOARD */}
      {board && (board.top?.length > 0 || board.my_rank) && (
        <Leaderboard board={board} testid="telepro-leaderboard"/>
      )}

      {/* RECENT SALES */}
      <div className="mb-10">
        <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
          <Trophy size={16} weight="duotone" className="text-primary"/> Mes dernières ventes
        </h2>
        {(!dash?.recent_sales || dash.recent_sales.length === 0) ? (
          <div className="bg-muted/30 border border-border rounded-sm p-6 text-center text-sm text-muted-foreground">
            Aucune vente attribuée pour le moment. Continue à appeler — ta première vente arrive !
          </div>
        ) : (
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[520px]">
              <thead className="bg-muted/40 text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Entreprise</th>
                  <th className="text-left px-3 py-2 font-semibold">Plan</th>
                  <th className="text-right px-3 py-2 font-semibold">Vente</th>
                  <th className="text-right px-3 py-2 font-semibold">Mensuel</th>
                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {dash.recent_sales.map((s) => (
                  <tr key={s.id} className="border-t border-border" data-testid={`sale-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{s.business_name}</td>
                    <td className="px-3 py-2"><PlanChip plan={s.plan}/></td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.sale_commission_eur?.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{s.monthly_commission_eur?.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-muted-foreground">{(s.sale_date || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* HISTORY */}
      <div>
        <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
          <ListChecks size={16} weight="duotone" className="text-primary"/> Historique de mes listes
        </h2>        {lists.length === 0 ? (
          <p className="text-sm text-muted-foreground">Pas encore d&apos;historique.</p>
        ) : (
          <div className="bg-card border border-border rounded-sm divide-y divide-border">
            {lists.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-accent/40 transition" data-testid={`list-row-${l.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Calendar size={14} weight="duotone" className="text-muted-foreground shrink-0"/>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{l.date}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {l.count} entreprises · {l.email_status === "sent" ? "Email envoyé" : l.email_status === "pending" ? "En attente" : l.email_status}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => downloadPdf(l.id, me?.code, l.date)}
                  className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1"
                  data-testid={`download-${l.id}`}
                >
                  <FilePdf size={12} weight="bold"/> PDF <ArrowRight size={10} weight="bold"/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PROSPECTION TUTORIAL */}
      <div className="mt-10" data-testid="telepro-tutorial-block">
        <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
          <BookOpen size={16} weight="duotone" className="text-primary"/> Guide de prospection
        </h2>
        <div className="bg-primary/8 border-l-4 border-primary rounded-sm p-3 mb-3">
          <p className="text-xs leading-relaxed">
            <b className="text-primary">📖 À LIRE EN PRIORITÉ — Nouveau chapitre « Comment utiliser le logiciel » ajouté.</b>
            {" "}Section 0 (tout en haut) : interface du portail, boutons Vente / Rappeler / Refus, gestes swipe mobile,
            timer d&apos;appel, HUD, undo, focus mode, badges de priorité… <b>Tout ce dont tu as besoin pour maîtriser l&apos;outil.</b>
          </p>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Script complet, réponses aux objections, closing. Lis-le entièrement au moins une fois, et reviens-y entre deux appels difficiles.
        </p>
        <TeleproTutorial/>
      </div>

      {/* PARTNERSHIP CONTRACT */}
      <div className="mt-10" id="contract-section">
        <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
          <ShieldCheck size={16} weight="duotone" className="text-primary"/> Contrat de partenariat
        </h2>
        <p className="text-xs text-muted-foreground mb-3">
          Contrat commercial indépendant entre toi et Digital Corp France.
          Lis-le, signe-le électroniquement, et reçois ta copie PDF par email.
        </p>
        <div className="mb-4">
          <TeleproSiretCard me={me} onUpdated={(fresh) => setMe(fresh)}/>
        </div>
        <TeleproContract onSigned={() => {
          // After signing, refresh `me` so the orange banner disappears
          teleproApi.get("/me").then(r => setMe(r.data.telepro)).catch(() => {});
        }}/>
      </div>

      {/* TIPS */}
      <div className="mt-10 bg-secondary/5 border border-secondary/30 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Phone size={18} weight="duotone" className="text-secondary shrink-0 mt-0.5"/>
          <div className="text-xs leading-relaxed">
            <b className="text-foreground">Astuce :</b> avant chaque appel, recherche rapidement le commerçant sur Google
            avec son nom + sa ville pour trouver son téléphone, son email ou son site web — l&apos;annuaire SIREN ne contient
            pas toujours ces infos. Si tu décroches une vente, transmets ton code <b className="text-foreground font-mono">{me?.code}</b> au
            commerçant : il le saisira dans le formulaire d&apos;inscription.
          </div>
        </div>
      </div>

      {/* DANGER ZONE */}
      <div className="mt-10 pt-6 border-t border-border">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Compte</h3>
        <button
          onClick={deactivateSelf}
          className="text-xs text-secondary hover:text-secondary-foreground hover:bg-secondary border border-secondary/40 rounded-sm px-3 py-2 inline-flex items-center gap-2 transition-colors"
          data-testid="telepro-deactivate-self"
        >
          <UserMinus size={12} weight="bold"/> Quitter la team (désactiver mon compte)
        </button>
        <p className="text-[11px] text-muted-foreground mt-2">
          Ton compte sera désactivé. Tu pourras demander à un administrateur de le réactiver plus tard.
        </p>
      </div>
      <EndOfDayRecapModal
        open={endOfDayOpen}
        onClose={() => setEndOfDayOpen(false)}
      />
      <EndOfWeekRecapModal
        open={endOfWeekOpen}
        onClose={() => setEndOfWeekOpen(false)}
      />

      {/* Vague 13 — Bottom nav mobile (masqué sur ≥ sm) */}
      <MobileBottomNav
        onOpenEndOfDay={() => setEndOfDayOpen(true)}
        hasToday={!!today}
      />
    </div>
  );
}

function Kpi({ label, value, icon, accent, testid }) {
  return (
    <div
      className={`border rounded-sm p-3 ${accent ? "bg-primary/5 border-primary/30" : "bg-card border-border"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">
        {icon} {label}
      </div>
      <div className={`font-display text-xl sm:text-2xl font-medium tabular-nums ${accent ? "text-primary" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function PlanChip({ plan }) {
  const labels = {
    premium_promo: "Premium promo",
    premium_ai_promo: "Premium IA promo",
    premium: "Premium",
    premium_ai: "Premium IA",
  };
  const isAi = (plan || "").includes("ai");
  return (
    <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${isAi ? "bg-secondary/15 text-secondary" : "bg-primary/10 text-primary"}`}>
      {labels[plan] || plan}
    </span>
  );
}

function Leaderboard({ board, testid }) {
  const top = board.top || [];
  const myRank = board.my_rank;
  const myInTop = top.some((t) => t.is_me);
  // Podium ordering: 2nd, 1st, 3rd (center 1st higher) — but on mobile, plain rank order is clearer.
  return (
    <div className="mb-10" data-testid={testid}>
      <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
        <Crown size={18} weight="duotone" className="text-secondary"/> Classement de la semaine
      </h2>
      <div className="bg-card border border-border rounded-sm p-4">
        {top.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Aucune vente cette semaine pour le moment — sois le premier à marquer un point !
          </p>
        ) : (
          <ol className="space-y-2" data-testid="leaderboard-list">
            {top.map((row) => (
              <li
                key={row.code}
                className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-sm transition ${
                  row.is_me ? "bg-primary/10 border border-primary/40 ring-1 ring-primary/20" : "bg-muted/30"
                }`}
                data-testid={`leaderboard-row-${row.rank}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <RankBadge rank={row.rank}/>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {row.display_name}{row.is_me && <span className="ml-1.5 text-[10px] uppercase tracking-widest font-bold text-primary">— Toi</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono">{row.code}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-lg font-medium tabular-nums">{row.sales}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {row.sales > 1 ? "ventes" : "vente"}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
        {myRank && !myInTop && (
          <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground" data-testid="leaderboard-my-rank">
            Tu es <b className="text-foreground">{myRank}<sup>e</sup></b> cette semaine ({board.my_sales} vente{board.my_sales > 1 ? "s" : ""}). Encore {Math.max(0, (top[2]?.sales || 0) - board.my_sales + 1)} ventes pour entrer dans le top 3 !
          </div>
        )}
      </div>
    </div>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) {
    return <span className="w-9 h-9 grid place-items-center rounded-full bg-secondary text-secondary-foreground shrink-0" title="1ʳᵉ place"><Crown size={16} weight="fill"/></span>;
  }
  if (rank === 2) {
    return <span className="w-9 h-9 grid place-items-center rounded-full bg-muted-foreground/30 text-foreground shrink-0" title="2ᵉ place"><Medal size={16} weight="duotone"/></span>;
  }
  if (rank === 3) {
    return <span className="w-9 h-9 grid place-items-center rounded-full bg-[#C8795A]/30 text-[#C8795A] shrink-0" title="3ᵉ place"><Medal size={16} weight="duotone"/></span>;
  }
  return <span className="w-9 h-9 grid place-items-center rounded-full bg-muted text-muted-foreground shrink-0 font-mono text-xs font-bold">{rank}</span>;
}
